import { useNavigate } from 'react-router-dom';
import { Minus, Plus, ShoppingCart, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCents } from '@/lib/format';
import { useCartStore, cartTotalCents } from './cartStore';

export function CartDrawer() {
  const { items, isOpen, close, remove, setQuantity } = useCartStore();
  const navigate = useNavigate();
  const total = cartTotalCents(items);

  // Go to the full cart page where the customer chooses a payment method and
  // can apply a voucher — don't jump straight to a payment provider.
  function onCheckout() {
    close();
    navigate('/cart');
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 bg-black/60 transition-opacity ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={close}
        aria-hidden="true"
      />
      {/* Panel */}
      <aside
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-border bg-card shadow-xl transition-transform ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-label="Shopping cart"
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <ShoppingCart className="h-5 w-5 text-primary" /> Your Cart
          </h2>
          <button onClick={close} aria-label="Close cart" className="rounded p-1 hover:bg-accent">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <p className="mt-12 text-center text-muted-foreground">Your cart is empty.</p>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => {
                const price = item.variant.sale_price_in_cents ?? item.variant.price_in_cents;
                return (
                  <li key={item.variant.id} className="flex gap-3 rounded-lg border border-border p-3">
                    <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded bg-secondary text-2xl">
                      {item.product.image ? (
                        <img
                          src={item.product.image}
                          alt=""
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <span>{item.product.emoji ?? '🎮'}</span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{item.product.title}</p>
                      <p className="truncate text-xs text-muted-foreground">{item.variant.title}</p>
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setQuantity(item.variant.id, item.quantity - 1)}
                            className="rounded border border-border p-1 hover:bg-accent"
                            aria-label="Decrease quantity"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-6 text-center text-sm">{item.quantity}</span>
                          <button
                            onClick={() => setQuantity(item.variant.id, item.quantity + 1)}
                            className="rounded border border-border p-1 hover:bg-accent"
                            aria-label="Increase quantity"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <span className="text-sm font-semibold">
                          {formatCents(price * item.quantity)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => remove(item.variant.id)}
                      aria-label="Remove item"
                      className="self-start rounded p-1 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-lg font-bold">{formatCents(total)}</span>
            </div>
            <Button className="w-full" size="lg" onClick={onCheckout}>
              Checkout
            </Button>
            <p className="mt-2 text-center text-xs text-muted-foreground">
              Choose payment &amp; apply a voucher on the next step
            </p>
          </div>
        )}
      </aside>
    </>
  );
}
